const normalizePath = (path: string): string => path.replace(/\/$/, '');

const isSamePageLink = (link: HTMLAnchorElement): boolean =>
	link.origin === window.location.origin &&
	normalizePath(link.pathname) === normalizePath(window.location.pathname) &&
	link.search === window.location.search;

const queryHashTarget = (hash: string): HTMLElement | null => {
	if (!hash || hash === '#') {
		return null;
	}
	try {
		return document.querySelector(hash) as HTMLElement | null;
	} catch {
		return null;
	}
};

/** Matches hide-on-scroll threshold in main.tsx */
const HEADER_HIDE_SCROLL_THRESHOLD = 0;

/** `scroll-margin-top` from CSS / Tailwind `scroll-mt-*` on the anchor target. */
const getScrollMarginTop = (target: HTMLElement): number => {
	const value = getComputedStyle(target).scrollMarginTop;
	const parsed = Number.parseFloat(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Fixed header is shown when scrolling up and hidden when scrolling down past the threshold.
 * Offset only when the header will cover the top of the viewport at the destination.
 */
const getHeaderScrollOffset = (targetDocumentTop: number): number => {
	const headerEl = document.getElementById('site-header');
	if (!headerEl) {
		return 0;
	}

	const headerHeight = headerEl.getBoundingClientRect().height;
	const currentScrollY = window.scrollY;

	if (targetDocumentTop < currentScrollY) {
		return headerHeight;
	}

	if (targetDocumentTop > HEADER_HIDE_SCROLL_THRESHOLD) {
		return 0;
	}

	return headerHeight;
};

const scrollToElement = (
	target: HTMLElement,
	behavior: ScrollBehavior = 'smooth',
): void => {
	const targetDocumentTop = target.getBoundingClientRect().top + window.scrollY;
	const scrollMarginTop = getScrollMarginTop(target);
	const headerOffset = getHeaderScrollOffset(
		targetDocumentTop - scrollMarginTop,
	);
	const top = targetDocumentTop - headerOffset - scrollMarginTop;

	window.scrollTo({ behavior, top });
};

export type SmoothAnchorScrollOptions = {
	closeMobileMenu?: () => void;
};

export const initSmoothAnchorScroll = (
	options: SmoothAnchorScrollOptions = {},
): void => {
	const { closeMobileMenu } = options;

	const scrollToHashFromLocation = () => {
		const hash = window.location.hash;
		const target = queryHashTarget(hash);
		if (!target) {
			return;
		}

		scrollToElement(target, 'smooth');
	};

	if (window.location.hash && window.location.hash !== '#') {
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}
		window.scrollTo(0, 0);

		const runHashScroll = () => setTimeout(scrollToHashFromLocation, 100);

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', runHashScroll, {
				once: true,
			});
		} else {
			runHashScroll();
		}
		window.addEventListener('load', scrollToHashFromLocation, { once: true });
	}

	document.addEventListener('click', (e) => {
		const link = (e.target as HTMLElement).closest(
			'a',
		) as HTMLAnchorElement | null;
		if (!link) {
			return;
		}

		const hash = link.hash;
		if (!hash || hash === '#') {
			return;
		}

		if (!isSamePageLink(link)) {
			return;
		}

		const target = queryHashTarget(hash);
		if (!target) {
			return;
		}

		e.preventDefault();

		closeMobileMenu?.();

		scrollToElement(target, 'smooth');
	});
};
